import React, { KeyboardEvent, useEffect, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel as MuiFormLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FormLabel } from "../../components/form-label/FormLabel";
import { EGG_KNOWN_ACTIONS, EggRoutingRule, Flow, Tenant } from "../../types";
import { tenantService } from "../../services/tenantService";
import { flowService } from "../../services/flowService";
import styles from "./EggRuleForm.module.css";

// ─── Costanti ────────────────────────────────────────────────────────────────

const OPERATORS = [
  { value: "==", label: "== (uguale)" },
  { value: "!=", label: "!= (diverso)" },
  { value: ">", label: "> (maggiore)" },
  { value: "<", label: "< (minore)" },
  { value: ">=", label: ">= (maggiore o uguale)" },
  { value: "<=", label: "<= (minore o uguale)" },
  { value: "in", label: "in (uno dei valori, separati da virgola)" },
  { value: "not_in", label: "not_in (nessuno dei valori, separati da virgola)" },
];

const EGG_KNOWN_SET = new Set<string>(EGG_KNOWN_ACTIONS);

// Root ammesse per un parametro dinamico: sono le chiavi di primo livello che
// EGG invia dentro payload.data (data → create, newData/oldData → update).
const TEMPLATE_PARAM_SOURCES = [
  { value: "data", label: "data" },
  { value: "newData", label: "newData" },
  { value: "oldData", label: "oldData" },
];

// Path di default del canale WhatsApp (AISA): firstMessage.template.parameters.
const DEFAULT_TEMPLATE_PARAMS_PATH = "firstMessage.template.parameters";

// ─── Schema Zod ──────────────────────────────────────────────────────────────

const conditionSchema = z.object({
  field: z.string().min(1, "Campo obbligatorio"),
  operator: z.enum(["==", "!=", ">", "<", ">=", "<=", "in", "not_in"] as const),
  value: z.string().min(1, "Valore obbligatorio"),
});

const headerSchema = z.object({
  key: z.string().min(1, "Chiave obbligatoria"),
  value: z.string(),
});

const mappingRowSchema = z.object({
  source: z.string().min(1, "Campo obbligatorio"),
  target: z.string().min(1, "Campo obbligatorio"),
});

const extraFieldRowSchema = z.object({
  key: z.string().min(1, "Campo obbligatorio"),
  value: z.string().min(1, "Campo obbligatorio"),
});

// Riga della lista ordinata di parametri template. I campi non pertinenti al
// tipo selezionato restano nel form ma non vengono inviati (validati nel
// superRefine dello schema, che li richiede solo per il tipo attivo).
const templateParamRowSchema = z.object({
  type: z.enum(["static", "dynamic"] as const),
  value: z.string(),
  source: z.enum(["data", "newData", "oldData"] as const),
  field: z.string(),
});

const formSchema = z
  .object({
    name: z.string().min(2, "Minimo 2 caratteri"),
    priority: z.coerce.number().int().min(0, "Valore minimo: 0"),
    active: z.boolean(),
    event_types: z.array(z.string()).min(1, "Seleziona almeno un tipo di evento"),
    match_mode: z.enum(["all", "any"] as const),
    conditions: z.array(conditionSchema),
    target_type: z.enum([
      "chatbot_flow",
      "external_webhook",
      "egg_finance_update",
    ] as const),
    cf_tenant_id: z.string().optional(),
    cf_flow_id: z.string().optional(),
    cf_phone_field: z.string().optional(),
    ew_url: z.string().optional(),
    ew_http_method: z.enum(["POST", "PATCH", "PUT"] as const).optional(),
    ew_headers: z.array(headerSchema).optional(),
    ew_mapping: z.array(mappingRowSchema).optional(),
    ew_mapping_mode: z.enum(["replace", "merge"] as const).optional(),
    ew_extra_fields: z.array(extraFieldRowSchema).optional(),
    wa_template_path: z.string().optional(),
    wa_template_params: z.array(templateParamRowSchema).optional(),
    fin_entity: z.string().optional(),
    fin_entity_id_field: z.string().optional(),
    fin_http_method: z.enum(["PATCH", "PUT"] as const).optional(),
    fin_x_egg_user: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.target_type === "chatbot_flow") {
      if (!data.cf_tenant_id?.trim()) {
        ctx.addIssue({ code: "custom", path: ["cf_tenant_id"], message: "Campo obbligatorio" });
      }
      if (!data.cf_flow_id?.trim()) {
        ctx.addIssue({ code: "custom", path: ["cf_flow_id"], message: "Campo obbligatorio" });
      }
      if (!data.cf_phone_field?.trim()) {
        ctx.addIssue({ code: "custom", path: ["cf_phone_field"], message: "Campo obbligatorio" });
      }
    }
    if (data.target_type === "external_webhook") {
      if (!data.ew_url?.trim()) {
        ctx.addIssue({ code: "custom", path: ["ew_url"], message: "URL obbligatorio" });
      } else {
        try {
          new URL(data.ew_url);
        } catch {
          ctx.addIssue({ code: "custom", path: ["ew_url"], message: "URL non valido" });
        }
      }
    }
    if (data.target_type === "egg_finance_update") {
      if (!data.fin_entity_id_field?.trim()) {
        ctx.addIssue({ code: "custom", path: ["fin_entity_id_field"], message: "Campo obbligatorio" });
      }
    }
    // Mapping condiviso tra external_webhook ed egg_finance_update
    if (
      (data.target_type === "external_webhook" ||
        data.target_type === "egg_finance_update") &&
      data.ew_mapping
    ) {
      data.ew_mapping.forEach((row, i) => {
        if (!row.source.trim()) {
          ctx.addIssue({ code: "custom", path: [`ew_mapping.${i}.source`], message: "Campo obbligatorio" });
        }
        if (!row.target.trim()) {
          ctx.addIssue({ code: "custom", path: [`ew_mapping.${i}.target`], message: "Campo obbligatorio" });
        }
      });
    }
    // Parametri template WhatsApp — solo external_webhook, solo se configurati
    if (data.target_type === "external_webhook" && (data.wa_template_params?.length ?? 0) > 0) {
      if (!data.wa_template_path?.trim()) {
        ctx.addIssue({
          code: "custom",
          path: ["wa_template_path"],
          message: "Obbligatorio quando ci sono parametri",
        });
      }
      data.wa_template_params!.forEach((row, i) => {
        if (row.type === "static" && !row.value.trim()) {
          ctx.addIssue({
            code: "custom",
            path: [`wa_template_params.${i}.value`],
            message: "Valore obbligatorio",
          });
        }
        if (row.type === "dynamic" && !row.field.trim()) {
          ctx.addIssue({
            code: "custom",
            path: [`wa_template_params.${i}.field`],
            message: "Campo obbligatorio",
          });
        }
      });
    }
  });

export type EggRuleFormData = z.infer<typeof formSchema>;

// ─── Utility: converte EggRoutingRule → EggRuleFormData ─────────────────────

export function ruleToFormData(rule: EggRoutingRule): EggRuleFormData {
  const base: EggRuleFormData = {
    name: rule.name,
    priority: rule.priority,
    active: rule.active,
    event_types: rule.event_types,
    match_mode: rule.match_mode,
    conditions: rule.conditions.map((c) => ({
      field: c.field,
      operator: c.operator,
      value: Array.isArray(c.value) ? c.value.join(", ") : String(c.value),
    })),
    target_type: rule.target_type,
  };

  if (rule.target_type === "chatbot_flow" && rule.target_chatbot_flow) {
    base.cf_tenant_id = rule.target_chatbot_flow.tenant_id;
    base.cf_flow_id = rule.target_chatbot_flow.flow_id;
    base.cf_phone_field = rule.target_chatbot_flow.phone_field;
  } else if (rule.target_type === "external_webhook" && rule.target_external_webhook) {
    const t = rule.target_external_webhook;
    base.ew_url = t.url;
    base.ew_http_method = t.http_method ?? "POST";
    base.ew_headers = t.headers
      ? Object.entries(t.headers).map(([key, value]) => ({ key, value }))
      : [];
    base.ew_mapping = rule.mapping
      ? Object.entries(rule.mapping).map(([source, target]) => ({ source, target }))
      : [];
    base.ew_mapping_mode = rule.mapping_mode ?? "replace";
    base.ew_extra_fields = rule.extra_fields
      ? Object.entries(rule.extra_fields).map(([key, value]) => ({ key, value }))
      : [];
    base.wa_template_path =
      rule.whatsapp_template?.parameters_path ?? DEFAULT_TEMPLATE_PARAMS_PATH;
    base.wa_template_params = (rule.whatsapp_template?.parameters ?? []).map((p) =>
      p.type === "static"
        ? { type: "static" as const, value: String(p.value), source: "data" as const, field: "" }
        : { type: "dynamic" as const, value: "", source: p.source, field: p.field }
    );
  } else if (
    rule.target_type === "egg_finance_update" &&
    rule.target_egg_finance_update
  ) {
    const t = rule.target_egg_finance_update;
    base.fin_entity = t.entity ?? "lead";
    base.fin_entity_id_field = t.entity_id_field;
    base.fin_http_method = t.http_method ?? "PATCH";
    base.fin_x_egg_user = t.x_egg_user ?? "";
    base.ew_mapping = rule.mapping
      ? Object.entries(rule.mapping).map(([source, target]) => ({ source, target }))
      : [];
    base.ew_mapping_mode = rule.mapping_mode ?? "replace";
    base.ew_extra_fields = rule.extra_fields
      ? Object.entries(rule.extra_fields).map(([key, value]) => ({ key, value }))
      : [];
  }

  return base;
}

// ─── Utility: converte EggRuleFormData → payload API ────────────────────────

export function formDataToPayload(
  data: EggRuleFormData,
  opts: { forUpdate?: boolean } = {}
) {
  const { forUpdate = false } = opts;

  const conditions = data.conditions.map((c) => {
    const isArray = c.operator === "in" || c.operator === "not_in";
    const value = isArray
      ? c.value.split(",").map((v) => v.trim()).filter(Boolean)
      : c.value;
    return { field: c.field, operator: c.operator, value };
  });

  const payload: Record<string, unknown> = {
    name: data.name,
    priority: data.priority,
    active: data.active,
    event_types: data.event_types,
    match_mode: data.match_mode,
    conditions,
    target_type: data.target_type,
  };

  // Mapping + extra fields sono condivisi tra external_webhook ed egg_finance_update.
  const buildMapping = () =>
    Object.fromEntries(
      (data.ew_mapping ?? [])
        .filter((r) => r.source.trim() && r.target.trim())
        .map((r) => [r.source, r.target])
    );
  const buildExtra = () =>
    Object.fromEntries(
      (data.ew_extra_fields ?? [])
        .filter((r) => r.key.trim() && r.value.trim())
        .map((r) => [r.key, r.value])
    );

  if (data.target_type === "chatbot_flow") {
    payload.target_chatbot_flow = {
      tenant_id: data.cf_tenant_id,
      flow_id: data.cf_flow_id,
      phone_field: data.cf_phone_field,
    };
  } else if (data.target_type === "egg_finance_update") {
    const financeTarget: Record<string, unknown> = {
      entity: data.fin_entity?.trim() || "lead",
      entity_id_field: data.fin_entity_id_field,
      http_method: data.fin_http_method ?? "PATCH",
    };
    if (data.fin_x_egg_user?.trim()) {
      financeTarget.x_egg_user = data.fin_x_egg_user.trim();
    }
    payload.target_egg_finance_update = financeTarget;

    const mapping = buildMapping();
    if (forUpdate || Object.keys(mapping).length > 0) {
      payload.mapping = mapping;
    }
    // Default "replace" = comportamento storico: in creazione si invia solo "merge"; in update sempre, per poter tornare a "replace".
    if (forUpdate || data.ew_mapping_mode === "merge") {
      payload.mapping_mode = data.ew_mapping_mode ?? "replace";
    }
    const extra = buildExtra();
    if (forUpdate || Object.keys(extra).length > 0) {
      payload.extra_fields = extra;
    }
  } else {
    const headers: Record<string, string> = {};
    (data.ew_headers ?? []).forEach(({ key, value }) => {
      if (key.trim()) headers[key.trim()] = value;
    });

    const webhookTarget: Record<string, unknown> = { url: data.ew_url };
    if (forUpdate || Object.keys(headers).length > 0) {
      webhookTarget.headers = headers;
    }
    if (data.ew_http_method && data.ew_http_method !== "POST") {
      webhookTarget.http_method = data.ew_http_method;
    }
    payload.target_external_webhook = webhookTarget;

    const mapping = buildMapping();
    if (forUpdate || Object.keys(mapping).length > 0) {
      payload.mapping = mapping;
    }
    // Default "replace" = comportamento storico: in creazione si invia solo "merge"; in update sempre, per poter tornare a "replace".
    if (forUpdate || data.ew_mapping_mode === "merge") {
      payload.mapping_mode = data.ew_mapping_mode ?? "replace";
    }
    const extra = buildExtra();
    if (forUpdate || Object.keys(extra).length > 0) {
      payload.extra_fields = extra;
    }

    // Parametri template WhatsApp: l'ordine delle righe è l'ordine nel payload
    // ({{1}} = prima riga). Le righe incomplete vengono scartate.
    const templateParams = (data.wa_template_params ?? [])
      .filter((r) => (r.type === "static" ? r.value.trim() : r.field.trim()))
      .map((r) =>
        r.type === "static"
          ? { type: "static", value: r.value }
          : { type: "dynamic", source: r.source, field: r.field.trim() }
      );
    if (forUpdate || templateParams.length > 0) {
      payload.whatsapp_template = {
        parameters_path: data.wa_template_path?.trim() || DEFAULT_TEMPLATE_PARAMS_PATH,
        parameters: templateParams,
      };
    }
  }

  return payload;
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface Props {
  defaultValues?: EggRuleFormData;
  onSubmit: (data: EggRuleFormData) => Promise<void>;
  isLoading: boolean;
  submitLabel: string;
  submitError?: string | null;
}

// ─── Componente ──────────────────────────────────────────────────────────────

export default function EggRuleForm({
  defaultValues,
  onSubmit,
  isLoading,
  submitLabel,
  submitError,
}: Props) {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<EggRuleFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues ?? {
      name: "",
      priority: 10,
      active: true,
      event_types: [],
      match_mode: "all",
      conditions: [],
      target_type: "external_webhook",
      cf_tenant_id: "",
      cf_flow_id: "",
      cf_phone_field: "",
      ew_url: "",
      ew_http_method: "POST",
      ew_headers: [],
      ew_mapping: [],
      ew_mapping_mode: "replace",
      ew_extra_fields: [],
      wa_template_path: DEFAULT_TEMPLATE_PARAMS_PATH,
      wa_template_params: [],
      fin_entity: "lead",
      fin_entity_id_field: "",
      fin_http_method: "PATCH",
      fin_x_egg_user: "",
    },
  });

  useEffect(() => {
    if (defaultValues) reset(defaultValues);
  }, [defaultValues, reset]);

  const targetType = watch("target_type");
  const selectedEvents = watch("event_types") ?? [];
  const cfTenantId = watch("cf_tenant_id") ?? "";
  const cfFlowId = watch("cf_flow_id") ?? "";

  // ── Tenant/Flow: dropdown dipendenti per il target chatbot_flow ──
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [flows, setFlows] = useState<Flow[]>([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [loadingFlows, setLoadingFlows] = useState(false);
  const [tenantError, setTenantError] = useState<string | null>(null);
  const [flowError, setFlowError] = useState<string | null>(null);

  // Carica i tenant (lazy) la prima volta che serve il target chatbot_flow.
  // Guard via ref: la dipendenza è solo targetType, così il setLoadingTenants
  // interno NON ri-triggera l'effect (evita che il cleanup annulli la fetch).
  const tenantsRequested = useRef(false);
  useEffect(() => {
    if (targetType !== "chatbot_flow" || tenantsRequested.current) return;
    tenantsRequested.current = true;
    let active = true;
    setLoadingTenants(true);
    setTenantError(null);
    tenantService
      .list()
      .then((res) => {
        if (active) setTenants(res.data.data.tenants.filter((t) => t.active));
      })
      .catch((e) => {
        console.error(e);
        if (active) {
          setTenantError("Errore nel caricamento dei tenant");
          tenantsRequested.current = false; // consente un nuovo tentativo
        }
      })
      .finally(() => {
        if (active) setLoadingTenants(false);
      });
    return () => {
      active = false;
    };
  }, [targetType]);

  // Carica i flow del tenant selezionato; ricarica al cambio tenant.
  useEffect(() => {
    if (targetType !== "chatbot_flow" || !cfTenantId) {
      setFlows([]);
      return;
    }
    let active = true;
    setLoadingFlows(true);
    setFlowError(null);
    flowService
      .list(cfTenantId)
      .then((res) => {
        if (active) setFlows(res.data.data.flows);
      })
      .catch((e) => {
        console.error(e);
        if (active) setFlowError("Errore nel caricamento dei flow");
      })
      .finally(() => {
        if (active) setLoadingFlows(false);
      });
    return () => {
      active = false;
    };
  }, [targetType, cfTenantId]);

  const {
    fields: conditionFields,
    append: appendCondition,
    remove: removeCondition,
  } = useFieldArray({ control, name: "conditions" });

  const {
    fields: headerFields,
    append: appendHeader,
    remove: removeHeader,
  } = useFieldArray({ control, name: "ew_headers" });

  const {
    fields: mappingFields,
    append: appendMapping,
    remove: removeMapping,
  } = useFieldArray({ control, name: "ew_mapping" });

  const {
    fields: extraFields,
    append: appendExtra,
    remove: removeExtra,
  } = useFieldArray({ control, name: "ew_extra_fields" });

  const {
    fields: templateParamFields,
    append: appendTemplateParam,
    remove: removeTemplateParam,
    move: moveTemplateParam,
  } = useFieldArray({ control, name: "wa_template_params" });

  const templateParams = watch("wa_template_params") ?? [];

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      {/* ── Sezione 1: Informazioni base ─── */}
      <Box className={styles.section}>
        <Typography variant="subtitle1" className={styles.sectionTitle}>
          Informazioni base
        </Typography>

        <Box className={styles.row}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Nome regola"
                required
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />
        </Box>

        <Box className={styles.rowSplit}>
          <Controller
            name="priority"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Priorità (0 = massima)"
                type="number"
                required
                inputProps={{ min: 0 }}
                error={!!errors.priority}
                helperText={errors.priority?.message ?? "Valore più basso = viene valutata prima"}
              />
            )}
          />

          <Controller
            name="active"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Switch checked={field.value} onChange={field.onChange} />}
                label="Regola attiva"
              />
            )}
          />
        </Box>
      </Box>

      <Divider />

      {/* ── Sezione 2: Tipi evento ─── */}
      <Box className={styles.section}>
        <Typography variant="subtitle1" className={styles.sectionTitle}>
          Tipi di evento EGG
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Inserisci uno o più event type. Premi Invio o virgola per aggiungere un tag.
        </Typography>
        <Controller
          name="event_types"
          control={control}
          render={({ field }) => (
            <Autocomplete
              multiple
              freeSolo
              options={[...EGG_KNOWN_ACTIONS]}
              value={field.value}
              onChange={(_, newValue) => {
                setValue("event_types", newValue as string[], { shouldValidate: true });
              }}
              onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                if (e.key === "," || e.key === "Enter") {
                  const input = (e.target as HTMLInputElement).value?.trim();
                  if (input && !field.value.includes(input)) {
                    setValue("event_types", [...field.value, input], { shouldValidate: true });
                    (e.target as HTMLInputElement).value = "";
                  }
                  e.preventDefault();
                }
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const isKnown = EGG_KNOWN_SET.has(option);
                  return (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                      color={isKnown ? "primary" : "default"}
                      sx={{ fontFamily: "monospace", fontSize: "0.72rem" }}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Event types"
                  placeholder={field.value.length === 0 ? "es. lead.create.pre" : ""}
                  error={!!errors.event_types}
                  helperText={errors.event_types?.message}
                />
              )}
            />
          )}
        />
        {(() => {
          const unknownEvents = selectedEvents.filter((e) => !EGG_KNOWN_SET.has(e));
          if (unknownEvents.length === 0) return null;
          return (
            <Alert
              severity="warning"
              icon={<WarningAmberIcon fontSize="inherit" />}
              sx={{ mt: 1.5 }}
            >
              Questi eventi non sono tra quelli predefiniti e verranno comunque salvati:{" "}
              {unknownEvents.map((ev, i) => (
                <React.Fragment key={ev}>
                  {i > 0 && ", "}
                  <code>{ev}</code>
                </React.Fragment>
              ))}
              . Verifica che il nome corrisponda a un evento realmente inviato da EGG.
            </Alert>
          );
        })()}
      </Box>

      <Divider />

      {/* ── Sezione 3: Condizioni ─── */}
      <Box className={styles.section}>
        <Typography variant="subtitle1" className={styles.sectionTitle}>
          Condizioni di routing
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Controller
            name="match_mode"
            control={control}
            render={({ field }) => (
              <FormControl component="fieldset">
                <MuiFormLabel component="legend">Modalità di corrispondenza</MuiFormLabel>
                <RadioGroup row {...field}>
                  <FormControlLabel value="all" control={<Radio />} label="Tutte (AND)" />
                  <FormControlLabel value="any" control={<Radio />} label="Almeno una (OR)" />
                </RadioGroup>
              </FormControl>
            )}
          />
        </Box>

        {conditionFields.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Nessuna condizione — la regola si applica a tutti gli eventi del tipo selezionato.
          </Typography>
        )}

        {conditionFields.map((condField, index) => (
          <Box key={condField.id} className={styles.conditionRow}>
            <Controller
              name={`conditions.${index}.field`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Campo (dot-notation)"
                  placeholder="data.provincia"
                  size="small"
                  error={!!errors.conditions?.[index]?.field}
                  helperText={errors.conditions?.[index]?.field?.message}
                  sx={{ flex: 2 }}
                />
              )}
            />

            <Controller
              name={`conditions.${index}.operator`}
              control={control}
              render={({ field }) => (
                <FormControl size="small" sx={{ flex: 1.5 }}>
                  <InputLabel>Operatore</InputLabel>
                  <Select {...field} label="Operatore">
                    {OPERATORS.map((op) => (
                      <MenuItem key={op.value} value={op.value}>
                        <Typography variant="body2">{op.label}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <Controller
              name={`conditions.${index}.value`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Valore"
                  size="small"
                  placeholder='es. "Roma" oppure "MI, RM, NA"'
                  error={!!errors.conditions?.[index]?.value}
                  helperText={errors.conditions?.[index]?.value?.message}
                  sx={{ flex: 2 }}
                />
              )}
            />

            <IconButton
              color="error"
              onClick={() => removeCondition(index)}
              title="Rimuovi condizione"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          size="small"
          sx={{ mt: 1 }}
          onClick={() => appendCondition({ field: "", operator: "==", value: "" })}
        >
          Aggiungi condizione
        </Button>
      </Box>

      <Divider />

      {/* ── Sezione 4: Target ─── */}
      <Box className={styles.section}>
        <Typography variant="subtitle1" className={styles.sectionTitle}>
          Destinazione
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Controller
            name="target_type"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Tipo destinazione</InputLabel>
                <Select {...field} label="Tipo destinazione">
                  <MenuItem value="chatbot_flow">Flusso chatbot interno</MenuItem>
                  <MenuItem value="external_webhook">Webhook esterno (partner)</MenuItem>
                  <MenuItem value="egg_finance_update">Aggiorna lead EGG Finance (PATCH)</MenuItem>
                </Select>
              </FormControl>
            )}
          />
        </Box>

        {targetType === "chatbot_flow" && (
          <Box className={styles.targetGrid}>
            {(tenantError || flowError) && (
              <Alert severity="error" sx={{ gridColumn: "1 / -1" }}>
                {tenantError ?? flowError}
              </Alert>
            )}
            <Controller
              name="cf_tenant_id"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth required error={!!errors.cf_tenant_id}>
                  <InputLabel id="cf-tenant-label">Tenant</InputLabel>
                  <Select
                    {...field}
                    labelId="cf-tenant-label"
                    label="Tenant"
                    value={field.value ?? ""}
                    onChange={(e) => {
                      field.onChange(e);
                      // Cambiando tenant, il flow precedente non è più valido.
                      // Niente shouldValidate: pre-submit RHF non ri-valida on-change,
                      // quindi forzare qui l'errore lo lascerebbe appeso anche dopo
                      // aver scelto un flow valido.
                      setValue("cf_flow_id", "", { shouldDirty: true });
                    }}
                    endAdornment={
                      loadingTenants ? (
                        <InputAdornment position="end" sx={{ mr: 3 }}>
                          <CircularProgress size={18} />
                        </InputAdornment>
                      ) : null
                    }
                  >
                    <MenuItem value="" disabled>
                      Seleziona un tenant
                    </MenuItem>
                    {tenants.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors.cf_tenant_id?.message ?? "Tenant proprietario del flow"}
                  </FormHelperText>
                </FormControl>
              )}
            />
            <Controller
              name="cf_flow_id"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  required
                  error={!!errors.cf_flow_id}
                  disabled={!cfTenantId || loadingFlows}
                >
                  <InputLabel id="cf-flow-label">Flow</InputLabel>
                  <Select
                    {...field}
                    labelId="cf-flow-label"
                    label="Flow"
                    value={field.value ?? ""}
                    endAdornment={
                      loadingFlows ? (
                        <InputAdornment position="end" sx={{ mr: 3 }}>
                          <CircularProgress size={18} />
                        </InputAdornment>
                      ) : null
                    }
                  >
                    <MenuItem value="" disabled>
                      Seleziona un flow
                    </MenuItem>
                    {/* Fallback: se il flow salvato non è (ancora) nella lista, lo
                        mostriamo comunque per non perdere il valore e non generare
                        il warning MUI "out-of-range value". */}
                    {cfFlowId && !flows.some((f) => f.id === cfFlowId) && (
                      <MenuItem value={cfFlowId}>{cfFlowId}</MenuItem>
                    )}
                    {flows.map((f) => (
                      <MenuItem key={f.id} value={f.id}>
                        {f.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors.cf_flow_id?.message ??
                      (!cfTenantId
                        ? "Seleziona prima un tenant"
                        : flows.length === 0 && !loadingFlows
                          ? "Nessun flow disponibile per questo tenant"
                          : "Flow da avviare per il lead")}
                  </FormHelperText>
                </FormControl>
              )}
            />
            <Controller
              name="cf_phone_field"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Campo telefono (dot-path)"
                  required
                  fullWidth
                  placeholder="cellulare"
                  error={!!errors.cf_phone_field}
                  helperText={errors.cf_phone_field?.message ?? "Campo di data che contiene il numero di telefono del lead"}
                />
              )}
            />
          </Box>
        )}

        {targetType === "external_webhook" && (
          <Box>
            <Box className={styles.rowSplit} sx={{ mb: 2 }}>
              <Controller
                name="ew_url"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="URL webhook"
                    required
                    fullWidth
                    placeholder="https://partner.example.com/webhook"
                    error={!!errors.ew_url}
                    helperText={errors.ew_url?.message}
                  />
                )}
              />

              <Controller
                name="ew_http_method"
                control={control}
                render={({ field }) => (
                  <FormControl sx={{ minWidth: 140 }}>
                    <InputLabel>HTTP Method</InputLabel>
                    <Select {...field} label="HTTP Method">
                      <MenuItem value="POST">POST</MenuItem>
                      <MenuItem value="PATCH">PATCH</MenuItem>
                      <MenuItem value="PUT">PUT</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Box>

            {/* Header HTTP */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Header HTTP aggiuntivi (opzionale)
            </Typography>

            {headerFields.map((headerField, index) => (
              <Box key={headerField.id} className={styles.headerRow}>
                <Controller
                  name={`ew_headers.${index}.key`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Header"
                      size="small"
                      placeholder="X-Api-Key"
                      error={!!errors.ew_headers?.[index]?.key}
                      helperText={errors.ew_headers?.[index]?.key?.message}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <Controller
                  name={`ew_headers.${index}.value`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Valore"
                      size="small"
                      sx={{ flex: 2 }}
                    />
                  )}
                />
                <IconButton color="error" onClick={() => removeHeader(index)} title="Rimuovi header">
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              size="small"
              sx={{ mt: 1, mb: 3 }}
              onClick={() => appendHeader({ key: "", value: "" })}
            >
              Aggiungi header
            </Button>
          </Box>
        )}

        {targetType === "egg_finance_update" && (
          <Box>
            <Box className={styles.rowSplit} sx={{ mb: 2 }}>
              <Controller
                name="fin_entity"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Entity"
                    fullWidth
                    placeholder="lead"
                    helperText="Entity EGG Finance da aggiornare (default: lead)"
                  />
                )}
              />

              <Controller
                name="fin_http_method"
                control={control}
                render={({ field }) => (
                  <FormControl sx={{ minWidth: 140 }}>
                    <InputLabel>HTTP Method</InputLabel>
                    <Select {...field} label="HTTP Method">
                      <MenuItem value="PATCH">PATCH</MenuItem>
                      <MenuItem value="PUT">PUT</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Controller
                name="fin_entity_id_field"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Entity ID field (dot-path)"
                    required
                    fullWidth
                    placeholder="idmarketing_leads"
                    inputProps={{ style: { fontFamily: "monospace" } }}
                    error={!!errors.fin_entity_id_field}
                    helperText={
                      errors.fin_entity_id_field?.message ??
                      'Dot-path risolto contro payload.data → id iniettato nell\'URL (.../record/{id}). Campi diretti sotto data → "idmarketing_leads"; lead annidato (data.data.*) → "data.idmarketing_leads".'
                    }
                  />
                )}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Controller
                name="fin_x_egg_user"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="X-EGG-User (opzionale)"
                    fullWidth
                    helperText="Header X-EGG-User da inoltrare nella chiamata (facoltativo)"
                  />
                )}
              />
            </Box>
          </Box>
        )}

        {(targetType === "external_webhook" ||
          targetType === "egg_finance_update") && (
          <Box>
            {/* Field Mapping */}
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              Field Mapping (opzionale)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Rimappa i campi del payload EGG verso i nomi attesi dal partner.
            </Typography>

            <Controller
              name="ew_mapping_mode"
              control={control}
              render={({ field }) => (
                <Box sx={{ mb: 1.5 }}>
                  <RadioGroup row {...field} value={field.value ?? "replace"}>
                    <FormControlLabel
                      value="replace"
                      control={<Radio />}
                      label="Solo campi mappati (sostituisci)"
                    />
                    <FormControlLabel
                      value="merge"
                      control={<Radio />}
                      label="Payload originale + mapping (unisci)"
                    />
                  </RadioGroup>
                  <Typography variant="caption" color="text.secondary">
                    {field.value === "merge"
                      ? "Vengono inviati i campi non mappati del payload, i campi mappati con il nuovo nome (la chiave sorgente mappata non viene inviata) e gli extra fields."
                      : "Vengono inviati solo i campi mappati e gli extra fields."}
                  </Typography>
                </Box>
              )}
            />

            {mappingFields.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: "italic" }}>
                Nessun mapping configurato — verrà inoltrato il payload completo.
              </Typography>
            )}

            {mappingFields.map((mappingField, index) => (
              <Box key={mappingField.id} className={styles.headerRow}>
                <Controller
                  name={`ew_mapping.${index}.source`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Campo sorgente"
                      size="small"
                      placeholder="data.cellulare"
                      inputProps={{ style: { fontFamily: "monospace" } }}
                      error={!!errors.ew_mapping?.[index]?.source}
                      helperText={errors.ew_mapping?.[index]?.source?.message}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <TextField
                  value="→"
                  size="small"
                  disabled
                  sx={{ width: 40, "& .MuiInputBase-input": { textAlign: "center", p: 1 } }}
                />
                <Controller
                  name={`ew_mapping.${index}.target`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Campo destinazione"
                      size="small"
                      placeholder="phone"
                      inputProps={{ style: { fontFamily: "monospace" } }}
                      error={!!errors.ew_mapping?.[index]?.target}
                      helperText={errors.ew_mapping?.[index]?.target?.message}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <IconButton color="error" onClick={() => removeMapping(index)} title="Rimuovi mapping">
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              size="small"
              sx={{ mt: 1 }}
              onClick={() => appendMapping({ source: "", target: "" })}
            >
              Aggiungi campo mapping
            </Button>

            {/* Extra Fields */}
            <Typography variant="body2" fontWeight={600} sx={{ mt: 3, mb: 0.5 }}>
              Campi extra (opzionale)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Valori statici da iniettare direttamente nell'oggetto <code>data</code> del payload outbound.
            </Typography>

            {extraFields.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: "italic" }}>
                Nessun campo extra — il payload viene inoltrato senza aggiunte.
              </Typography>
            )}

            {extraFields.map((extraField, index) => (
              <Box key={extraField.id} className={styles.headerRow}>
                <Controller
                  name={`ew_extra_fields.${index}.key`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Campo"
                      size="small"
                      placeholder="nomeCampo"
                      inputProps={{ style: { fontFamily: "monospace" } }}
                      error={!!errors.ew_extra_fields?.[index]?.key}
                      helperText={errors.ew_extra_fields?.[index]?.key?.message}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <Controller
                  name={`ew_extra_fields.${index}.value`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Valore"
                      size="small"
                      placeholder="valoreStatico"
                      error={!!errors.ew_extra_fields?.[index]?.value}
                      helperText={errors.ew_extra_fields?.[index]?.value?.message}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <IconButton color="error" onClick={() => removeExtra(index)} title="Rimuovi campo">
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              size="small"
              sx={{ mt: 1 }}
              onClick={() => appendExtra({ key: "", value: "" })}
            >
              Aggiungi campo extra
            </Button>

            {/* Parametri template WhatsApp — solo canale WhatsApp */}
            {targetType === "external_webhook" && (
              <>
                <Typography variant="body2" fontWeight={600} sx={{ mt: 3, mb: 0.5 }}>
                  Parametri template WhatsApp (opzionale)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Lista ordinata: il primo parametro riempie <code>{"{{1}}"}</code>, il secondo{" "}
                  <code>{"{{2}}"}</code>, e così via. Un parametro dinamico legge il valore da{" "}
                  <code>payload.data</code> (path anche nested). Se un campo dinamico è assente o
                  vuoto l'evento va in errore e non viene inviato nulla.
                </Typography>

                {templateParamFields.length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, fontStyle: "italic" }}
                  >
                    Nessun parametro — il template viene inviato senza parametri.
                  </Typography>
                )}

                {templateParamFields.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Controller
                      name="wa_template_path"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Path dei parametri nel body"
                          size="small"
                          fullWidth
                          placeholder={DEFAULT_TEMPLATE_PARAMS_PATH}
                          inputProps={{ style: { fontFamily: "monospace" } }}
                          error={!!errors.wa_template_path}
                          helperText={
                            errors.wa_template_path?.message ??
                            "Dove scrivere l'array nel body outbound (dot-path, annida come il mapping)."
                          }
                        />
                      )}
                    />
                  </Box>
                )}

                {templateParamFields.map((paramField, index) => {
                  const rowType = templateParams[index]?.type ?? "static";
                  return (
                    <Box key={paramField.id} className={styles.headerRow}>
                      <Chip
                        label={`{{${index + 1}}}`}
                        size="small"
                        sx={{ fontFamily: "monospace", mt: 0.5 }}
                      />
                      <Controller
                        name={`wa_template_params.${index}.type`}
                        control={control}
                        render={({ field }) => (
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Tipo</InputLabel>
                            <Select {...field} label="Tipo">
                              <MenuItem value="static">Static</MenuItem>
                              <MenuItem value="dynamic">Dynamic</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      />

                      {rowType === "static" ? (
                        <Controller
                          name={`wa_template_params.${index}.value`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Valore"
                              size="small"
                              placeholder="valoreStatico"
                              error={!!errors.wa_template_params?.[index]?.value}
                              helperText={errors.wa_template_params?.[index]?.value?.message}
                              sx={{ flex: 1 }}
                            />
                          )}
                        />
                      ) : (
                        <>
                          <Controller
                            name={`wa_template_params.${index}.source`}
                            control={control}
                            render={({ field }) => (
                              <FormControl size="small" sx={{ minWidth: 130 }}>
                                <InputLabel>Source</InputLabel>
                                <Select {...field} label="Source">
                                  {TEMPLATE_PARAM_SOURCES.map((s) => (
                                    <MenuItem key={s.value} value={s.value}>
                                      {s.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                          />
                          <Controller
                            name={`wa_template_params.${index}.field`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Campo"
                                size="small"
                                placeholder="nome"
                                inputProps={{ style: { fontFamily: "monospace" } }}
                                error={!!errors.wa_template_params?.[index]?.field}
                                helperText={errors.wa_template_params?.[index]?.field?.message}
                                sx={{ flex: 1 }}
                              />
                            )}
                          />
                        </>
                      )}

                      <IconButton
                        onClick={() => moveTemplateParam(index, index - 1)}
                        disabled={index === 0}
                        title="Sposta su"
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => moveTemplateParam(index, index + 1)}
                        disabled={index === templateParamFields.length - 1}
                        title="Sposta giù"
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => removeTemplateParam(index)}
                        title="Rimuovi parametro"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  );
                })}

                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  size="small"
                  sx={{ mt: 1 }}
                  onClick={() =>
                    appendTemplateParam({
                      type: "static",
                      value: "",
                      source: "data",
                      field: "",
                    })
                  }
                >
                  Aggiungi parametro
                </Button>
              </>
            )}
          </Box>
        )}
      </Box>

      {submitError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {submitError}
        </Alert>
      )}

      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        <Button type="submit" variant="contained" disabled={isLoading}>
          {isLoading ? "Salvataggio..." : submitLabel}
        </Button>
      </Box>
    </form>
  );
}
